import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/axios";
import { formatRupiah } from "@/lib/utils";
import {
  getListJenisAset,
  getListKepemilikanAset,
  getListSumberAir,
  getListSumberListrik,
  getListTeganganListrikAset,
} from "@/services/getData";
import {
  DataAset,
  ErrorType,
  JenisAset,
  KepemilikanAsset,
  SumberAir,
  SumberListrik,
  TeganganListrikAset,
} from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Eye, FileText, Home, Info, Loader2, Zap } from "lucide-react"; // Tambahan icon Lucide
import { useState } from "react";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa6";
import { MdModeEdit } from "react-icons/md";
import { RiDeleteBack2Line } from "react-icons/ri";

const postData = async (newData: FormData) => {
  const response = await api.post(
    "/siswa/daftar-kembali/data-aset/store",
    newData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};
const patchData = async (newData: FormData) => {
  const response = await api.post(
    "/siswa/daftar-kembali/data-aset/update",
    newData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

const deleteData = async (deletedData: FormData) => {
  const id_data_aset = deletedData.get("id_data_aset");

  const response = await api({
    url: "/siswa/daftar-kembali/data-aset/delete",
    method: "DELETE",
    data: { id_data_aset },
  });
  return response.data;
};

export default function Form({
  method,
  closeModal,
  selectedRow,
  idDataPendaftaran,
}: {
  method: "POST" | "PATCH" | "DELETE";
  closeModal: () => void;
  selectedRow?: DataAset | null;
  idDataPendaftaran?: string;
}) {
  const isPost = method === "POST";
  const isPatch = method === "PATCH";
  const isDelete = method === "DELETE";

  const [error, setError] = useState<ErrorType | null>(null);
  const [form, setForm] = useState<DataAset>({
    id_data_aset: selectedRow?.id_data_aset || "",
    id_data_pendaftaran: idDataPendaftaran || "",
    id_ref_aset_jenis: selectedRow?.id_ref_aset_jenis || "",
    id_ref_aset_kepemilikan: selectedRow?.id_ref_aset_kepemilikan || "",
    id_ref_aset_tegangan_listrik:
      selectedRow?.id_ref_aset_tegangan_listrik || "",
    id_ref_aset_luas_tanah: selectedRow?.id_ref_aset_luas_tanah || "",
    id_ref_aset_luas_bangunan: selectedRow?.id_ref_aset_luas_bangunan || "",
    id_ref_aset_sumber_listrik: selectedRow?.id_ref_aset_sumber_listrik || "",
    id_ref_aset_sumber_air: selectedRow?.id_ref_aset_sumber_air || "",
    id_ref_aset_pembayaran_air: selectedRow?.id_ref_aset_pembayaran_air || "",
    aset_luas_tanah: selectedRow?.aset_luas_tanah || 0,
    aset_luas_bangunan: selectedRow?.aset_luas_bangunan || 0,
    pembayaran_air: selectedRow?.pembayaran_air || 0,
    path_berkas_listrik: selectedRow?.path_berkas_listrik || "",
    path_berkas_air: selectedRow?.path_berkas_air || "",
    path_berkas_pbb: selectedRow?.path_berkas_pbb || "",
  });
  const [filePembayaranListrik, setFilePembayaranListrik] =
    useState<File | null>(null);
  const [filePembayaranAir, setFilePembayaranAir] = useState<File | null>(null);
  const [filePbb, setFilePbb] = useState<File | null>(null);

  const [openJenisAset, setOpenJenisAset] = useState(false);
  const [openStatusKepemilikan, setOpenStatusKepemilikan] = useState(false);
  const [openSumberListrik, setOpenSumberListrik] = useState(false);
  const [openTeganganListrik, setOpenTeganganListrik] = useState(false);
  const [openSumberAir, setOpenSumberAir] = useState(false);

  // --- QUERIES ---
  const { data: dataJenisAset } = useQuery({
    queryKey: ["listJenisAset"],
    queryFn: () => getListJenisAset(),
    select: (data) =>
      data?.map((jenisAset: JenisAset) => ({
        label: jenisAset.nama_jenis_aset,
        value: `${jenisAset.id_ref_aset_jenis}`,
      })),
  });

  const { data: dataStatusKepemilikanAset } = useQuery({
    queryKey: ["listKepemilikanAset"],
    queryFn: () => getListKepemilikanAset(),
    select: (data) =>
      data?.map((statusKepemilikanAset: KepemilikanAsset) => ({
        label: statusKepemilikanAset.nama_kepemilikan,
        value: `${statusKepemilikanAset.id_ref_aset_kepemilikan}`,
      })),
  });

  const { data: dataTeganganListrik } = useQuery({
    queryKey: ["listTeganganListrikAset"],
    queryFn: () => getListTeganganListrikAset(),
    select: (data) =>
      data?.map((teganganListrik: TeganganListrikAset) => ({
        label: `${teganganListrik.tegangan_listrik} VA`,
        value: `${teganganListrik.id_ref_aset_tegangan_listrik}`,
      })),
  });

  const { data: dataSumberListrik } = useQuery({
    queryKey: ["listSumberListrik"],
    queryFn: () => getListSumberListrik(),
    select: (data) =>
      data?.map((sumberListrik: SumberListrik) => ({
        label: `${sumberListrik.sumber_listrik}`,
        value: `${sumberListrik.id_ref_aset_sumber_listrik}`,
      })),
  });

  const { data: dataSumberAir } = useQuery({
    queryKey: ["listSumberAir"],
    queryFn: () => getListSumberAir(),
    select: (data) =>
      data?.map((sumberAir: SumberAir) => ({
        label: `${sumberAir.sumber_air}`,
        value: `${sumberAir.id_ref_aset_sumber_air}`,
      })),
  });

  // --- MUTATION ---
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      queryClient.invalidateQueries({ queryKey: ["listDataAset"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      if (err.response?.data) {
        setError(err.response.data as ErrorType);
      } else {
        setError({
          message: "An unknown error occurred.",
          errors: {},
        });
      }
      toast.error(err.response?.data?.message || "An unknown error occurred.");
    },
  });

  // --- HANDLERS ---
  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFileState: React.Dispatch<React.SetStateAction<File | null>>,
  ) => {
    const file = e.target.files && e.target.files[0];
    const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error("Ukuran file maksimal 1MB.");
        e.target.value = "";
        return;
      }
      setFileState(file);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const formData = new FormData();

    if (!isPatch || isDelete) {
      formData.append("id_data_aset", String(selectedRow?.id_data_aset));
    }

    Object.entries(form).forEach(([key, value]) => {
      formData.append(key, String(value));
    });

    if (filePbb) formData.append("file_berkas_pbb", filePbb);
    if (filePembayaranListrik)
      formData.append("file_slip_pembayaran_listrik", filePembayaranListrik);
    if (filePembayaranAir)
      formData.append("file_slip_pembayaran_air", filePembayaranAir);

    mutate(formData);
  };

  // --- VIEW: DELETE ---
  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Hapus Data Aset"
          description={`Apakah anda yakin akan menghapus data aset seluas "${selectedRow?.aset_luas_bangunan} m²"?`}
        />
        <div className="flex gap-3 justify-end mt-4">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-2"
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Ya, Hapus Data"
            )}
          </Button>
        </div>
      </form>
    );

  // --- VIEW: INSERT / UPDATE ---
  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-6 animate-in fade-in duration-300"
    >
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Tambah Data Aset" : "Ubah Data Aset"}
      />

      <div className="flex flex-col gap-8">
        {/* ========================================================= */}
        {/* SECTION 1: RINCIAN ASET                                   */}
        {/* ========================================================= */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
            <Home className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-slate-800 dark:text-slate-200">
              Informasi Aset
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label required>Jenis Aset</Label>
              <Combobox
                open={openJenisAset}
                setOpen={setOpenJenisAset}
                value={form.id_ref_aset_jenis || ""}
                setValue={(newValue) =>
                  setForm((prev) => ({ ...prev, id_ref_aset_jenis: newValue }))
                }
                options={dataJenisAset}
                placeholder="Pilih jenis aset"
              />
              <InputError text={error?.errors?.id_ref_aset_jenis?.join(",")} />
            </div>

            <div>
              <Label required>Status Kepemilikan</Label>
              <Combobox
                open={openStatusKepemilikan}
                setOpen={setOpenStatusKepemilikan}
                value={form.id_ref_aset_kepemilikan || ""}
                setValue={(newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    id_ref_aset_kepemilikan: newValue,
                  }))
                }
                options={dataStatusKepemilikanAset}
                placeholder="Pilih status kepemilikan"
              />
              <InputError
                text={error?.errors?.id_ref_aset_kepemilikan?.join(",")}
              />
            </div>

            <div>
              <Label required>Luas Tanah (m²)</Label>
              <Input
                required
                placeholder="Contoh: 150"
                value={String(form.aset_luas_tanah)}
                onChange={(event) => {
                  const rawValue = event.target.value.replace(/\D/g, "");
                  setForm((prev) => ({
                    ...prev,
                    aset_luas_tanah: Number(rawValue),
                  }));
                }}
              />
              <InputError text={error?.errors?.aset_luas_tanah?.join(",")} />
            </div>

            <div>
              <Label required>Luas Bangunan (m²)</Label>
              <Input
                required
                placeholder="Contoh: 80"
                value={String(form.aset_luas_bangunan)}
                onChange={(event) => {
                  const rawValue = event.target.value.replace(/\D/g, "");
                  setForm((prev) => ({
                    ...prev,
                    aset_luas_bangunan: Number(rawValue),
                  }));
                }}
              />
              <InputError text={error?.errors?.aset_luas_bangunan?.join(",")} />
            </div>
          </div>
        </section>

        {/* ========================================================= */}
        {/* SECTION 2: UTILITAS & TAGIHAN                             */}
        {/* ========================================================= */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
            <Zap className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-slate-800 dark:text-slate-200">
              Listrik & Air
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label required>Sumber Listrik</Label>
              <Combobox
                open={openSumberListrik}
                setOpen={setOpenSumberListrik}
                value={form.id_ref_aset_sumber_listrik || ""}
                setValue={(newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    id_ref_aset_sumber_listrik: newValue,
                  }))
                }
                options={dataSumberListrik}
                placeholder="Pilih sumber listrik"
              />
              <InputError
                text={error?.errors?.id_ref_aset_sumber_listrik?.join(",")}
              />
            </div>

            <div>
              <Label required>Tegangan Listrik</Label>
              <Combobox
                open={openTeganganListrik}
                setOpen={setOpenTeganganListrik}
                value={form.id_ref_aset_tegangan_listrik || ""}
                setValue={(newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    id_ref_aset_tegangan_listrik: newValue,
                  }))
                }
                options={dataTeganganListrik}
                placeholder="Pilih tegangan listrik"
              />
              <InputError
                text={error?.errors?.id_ref_aset_tegangan_listrik?.join(",")}
              />
            </div>

            <div>
              <Label required>Sumber Air</Label>
              <Combobox
                open={openSumberAir}
                setOpen={setOpenSumberAir}
                value={form.id_ref_aset_sumber_air || ""}
                setValue={(newValue) =>
                  setForm((prev) => ({
                    ...prev,
                    id_ref_aset_sumber_air: newValue,
                  }))
                }
                options={dataSumberAir}
                placeholder="Pilih sumber air"
              />
              <InputError
                text={error?.errors?.id_ref_aset_sumber_air?.join(",")}
              />
            </div>

            <div>
              <Label required>Tagihan Air / Bulan</Label>
              <Input
                required
                placeholder="Nominal rupiah..."
                value={formatRupiah(Number(form.pembayaran_air))}
                onChange={(event) => {
                  const rawValue = event.target.value.replace(/\D/g, "");
                  setForm((prev) => ({ ...prev, pembayaran_air: rawValue }));
                }}
              />
              <InputError text={error?.errors?.pembayaran_air?.join(",")} />
            </div>
          </div>
        </section>

        {/* ========================================================= */}
        {/* SECTION 3: DOKUMEN PENDUKUNG                              */}
        {/* ========================================================= */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
            <FileText className="w-5 h-5 text-emerald-500" />
            <h3 className="font-semibold text-slate-800 dark:text-slate-200">
              Dokumen Pendukung
            </h3>
          </div>

          <div className="space-y-5">
            {/* FILE 1: PBB */}
            <div className="border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/20 rounded-xl p-4 sm:p-5 relative">
              <Label
                required={!form.path_berkas_pbb}
                className="flex items-center gap-2 mb-2 font-semibold"
              >
                Bukti Pembayaran PBB (Pajak Bumi & Bangunan)
              </Label>

              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50 p-3 rounded-lg mb-4 flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800 dark:text-blue-200/90 leading-relaxed">
                  Jika tidak ada, unggah{" "}
                  <b>Surat Keterangan dari Kepala Lingkungan/Desa</b> yang
                  memuat <b>keterangan pembayaran PBB</b>, luas tanah, dan luas
                  bangunan.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex-1 w-full">
                  <Input
                    required={!form.path_berkas_pbb}
                    type="file"
                    accept=".pdf, .jpg, .jpeg, .png"
                    onChange={(e) => handleFileChange(e, setFilePbb)}
                    className="bg-white dark:bg-slate-950 file:text-primary file:font-medium"
                  />
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Maks: 1MB | Format: PDF, JPG, PNG
                  </p>
                </div>
                {form.path_berkas_pbb && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 w-full sm:w-auto h-10 text-primary border-primary/20 hover:bg-primary/5"
                    onClick={() =>
                      window.open(form.path_berkas_pbb || "", "_blank")
                    }
                  >
                    <Eye className="w-4 h-4 mr-2" /> Lihat Dokumen PBB Saat Ini
                  </Button>
                )}
              </div>
              <InputError text={error?.errors?.file_berkas_pbb?.join(",")} />
            </div>

            {/* FILE 2: LISTRIK */}
            <div className="border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/20 rounded-xl p-4 sm:p-5 relative">
              <Label
                required={!form.path_berkas_listrik}
                className="flex items-center gap-2 mb-2 font-semibold"
              >
                Bukti Pembayaran Listrik Terakhir
              </Label>

              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/30 p-3 rounded-lg mb-4 flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <ul className="text-xs text-amber-800 dark:text-amber-200/90 leading-relaxed list-disc pl-4">
                  <li>
                    Jika numpang, unggah Surat Keterangan pembayaran listrik
                    dari Kepala Lingkungan/Desa.
                  </li>
                  <li>
                    Jika benar-benar tidak ada listrik, unggah Surat Keterangan
                    Tidak Memiliki Listrik dari Desa.
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex-1 w-full">
                  <Input
                    required={!form.path_berkas_listrik}
                    type="file"
                    accept=".pdf, .jpg, .jpeg, .png"
                    onChange={(e) =>
                      handleFileChange(e, setFilePembayaranListrik)
                    }
                    className="bg-white dark:bg-slate-950 file:text-primary file:font-medium"
                  />
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Maks: 1MB | Format: PDF, JPG, PNG
                  </p>
                </div>
                {form.path_berkas_listrik && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 w-full sm:w-auto h-10 text-primary border-primary/20 hover:bg-primary/5"
                    onClick={() =>
                      window.open(form.path_berkas_listrik || "", "_blank")
                    }
                  >
                    <Eye className="w-4 h-4 mr-2" /> Lihat Dokumen Listrik Saat
                    Ini
                  </Button>
                )}
              </div>
              <InputError
                text={error?.errors?.file_slip_pembayaran_listrik?.join(",")}
              />
            </div>

            {/* FILE 3: AIR */}
            <div className="border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/20 rounded-xl p-4 sm:p-5 relative">
              <Label
                required={!form.path_berkas_air}
                className="flex items-center gap-2 mb-2 font-semibold"
              >
                Bukti Pembayaran Air Terakhir
              </Label>

              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50 p-3 rounded-lg mb-4 flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <ul className="text-xs text-blue-800 dark:text-blue-200/90 leading-relaxed list-disc pl-4">
                  <li>
                    Jika menumpang air unggah Surat Keterangan dari Kepala
                    Lingkungan/Desa
                  </li>
                  <li>
                    Jika menggunakan sumur pribadi/tidak bayar air, unggah Surat
                    Keterangan Tidak Memiliki Pembayaran Air dari Desa.
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="flex-1 w-full">
                  <Input
                    required={!form.path_berkas_air}
                    type="file"
                    accept=".pdf, .jpg, .jpeg, .png"
                    onChange={(e) => handleFileChange(e, setFilePembayaranAir)}
                    className="bg-white dark:bg-slate-950 file:text-primary file:font-medium"
                  />
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Maks: 1MB | Format: PDF, JPG, PNG
                  </p>
                </div>
                {form.path_berkas_air && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 w-full sm:w-auto h-10 text-primary border-primary/20 hover:bg-primary/5"
                    onClick={() =>
                      window.open(form.path_berkas_air || "", "_blank")
                    }
                  >
                    <Eye className="w-4 h-4 mr-2" /> Lihat Dokumen Air Saat Ini
                  </Button>
                )}
              </div>
              <InputError
                text={error?.errors?.file_slip_pembayaran_air?.join(",")}
              />
            </div>
          </div>
        </section>
      </div>

      {/* Action Buttons */}
      <div className="mt-8 flex justify-end gap-3 pt-5 border-t border-slate-200 dark:border-slate-800">
        <Button
          type="button"
          variant="outline"
          onClick={closeModal}
          disabled={isPending}
        >
          Batal
        </Button>
        <Button type="submit" disabled={isPending} className="min-w-[160px]">
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Menyimpan...
            </>
          ) : isPost ? (
            "Simpan Data Aset"
          ) : (
            "Simpan Perubahan"
          )}
        </Button>
      </div>
    </form>
  );
}
